import Button from 'components/Form/Button';
import { CoreContext } from 'context/CoreContext';
import moment from 'moment';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { toast } from 'react-toastify';
import { Update, UpdateCredito } from 'services/banking';
import { Title, Touch } from 'ui/styled';
import { theme } from 'ui/theme-color';
import { capitalize } from 'utils';
import {
  optionsCarencia,
  optionsPrazoRenogicar,
  optionsSN,
  optionsTacTab,
} from 'utils/options';
import {
  parseCurrency,
  parseSNtoBoolean,
  unparseCurrency,
} from 'utils/parsers';
import { calculaFinanciamento, taxaCET } from 'utils/simuladorFin';
import FormCore from '../../Form/Core';
import Wrapper from '../Wrapper';
import {
  AddProposalContainer,
  CompanyButtonContainer,
  TimelineContainer,
  TimelineContent,
  TimelineItem,
} from './styled';
import { handleFormWithChecklist } from './utils';

export default function BankingNegocios({ dados, reload, onClose }) {
  const { user, setModal, reloadContent } = useContext(CoreContext);
  const refForm = useRef(null);
  const formRefParecer = useRef(null);
  const [loading, setLoading] = useState(false);
  const [id, setId] = useState(dados?.id || null);
  const [showParecer, setShowParecer] = useState(false);
  const [formOferta, setFormOferta] = useState([]);

  //se o distribuidor tiver regra de range para o banking vai trazer a regra se nao vai trazer o nosso padrao.
  const rangePrazo = !dados?.range_prazo
    ? optionsPrazoRenogicar
    : dados?.range_prazo;

  const calc = useCallback(async () => {
    if (!refForm.current) return;

    const formValues = refForm.current.getForm?.(true);
    if (!formValues) return;

    const taxa = dados?.taxas;

    if (!taxa) {
      return;
    }
    // valida se a taxa digitada esta entre perc_taxa_max e perc_taxa_min
    if (
      formValues.taxa < taxa.perc_taxa_min ||
      formValues.taxa > taxa.perc_taxa_max
    ) {
      toast.error(
        `A taxa aplicável deverá variar entre ${taxa.perc_taxa_min}% e ${taxa.perc_taxa_max}%. Valores serão recalculados com a taxa padrão.`
      );
      refForm.current.setItem(taxa.perc_taxa, 'taxa');
    }
    // Ajusta a taxa conforme o valor do campo taxa do formulário
    const taxaAjustada = {
      ...taxa,
      perc_taxa: parseFloat(formValues.taxa),
      valor_tac_fixo:
        formValues.tacSN === 'Sim' ? (dados?.valor_tac_fixo ?? 900) : 0,
    };

    const resultados = calculaFinanciamento(formValues, taxaAjustada);

    // Atualiza os campos do formulário com os resultados calculados
    refForm.current.setItem(
      parseCurrency(resultados.valorParcela),
      'valor_parcelas'
    );
    refForm.current.setItem(parseCurrency(resultados.valorIof), 'iof');
    refForm.current.setItem(
      parseCurrency(resultados.valorSeguroAssis),
      'valor_seguro_assis'
    );
    refForm.current.setItem(
      parseCurrency(resultados.valorSeguro),
      'valor_seguro'
    );
    refForm.current.setItem(parseCurrency(resultados.valorTab), 'tab');
    refForm.current.setItem(parseCurrency(resultados.valorTac), 'tac');
  }, [dados]);

  const calcCet = async () => {
    let prazo = parseInt(refForm.current.getItem('prazo_financiamento'));
    let pmt = unparseCurrency(refForm.current.getItem('valor_parcelas'));
    let valorFinan = unparseCurrency(
      refForm.current.getItem('valor_financiamento')
    );
    let cet = await taxaCET(prazo, pmt, valorFinan * -1);
    refForm.current.setItem(cet, 'cet');
  };

  const handleValorEntradaOnBlur = useCallback(async () => {
    if (!refForm.current) {
      return;
    }
    refForm.current.getItem('valor_entrada');
    let valorFin =
      unparseCurrency(refForm.current.getItem('valor_venda')) -
      refForm.current.getItem('valor_entrada');
    refForm.current.setItem(parseCurrency(valorFin), 'valor_financiamento');
    setTimeout(async () => {
      await calc();
    }, 100);
    setTimeout(async () => await calcCet(), 100);
  }, [calc]);

  const handleFinancingRecalcOnBlur = useCallback(async () => {
    if (!refForm.current) {
      return;
    }
    await calc();
    await calcCet();
  }, [calc]);

  const handleInitializeForm = useCallback(() => {
    setId(dados?.id || null);
    if (!dados || !refForm.current?.setItem) {
      return;
    }
    //Valor total da venda
    refForm.current.setItem(dados.value, 'valor_venda');
    // Valor de entrada
    refForm.current.setItem(
      dados?.proposta_financeira?.propostas_banco[0]?.valor_entrada,
      'valor_entrada'
    );
    // Valor financiado
    refForm.current.setItem(
      parseCurrency(
        dados?.proposta_financeira?.informacoes_projeto?.custo_total_projeto -
          dados?.proposta_financeira?.propostas_banco[0]?.valor_entrada
      ),
      'valor_financiamento'
    );
    // Valor das parcelas, primeiramente valor calculado
    refForm.current.setItem(
      parseCurrency(dados?.calculos_financiamento?.valorParcela) ||
        parseCurrency(
          dados?.proposta_financeira?.propostas_banco[0]?.valor_parcelas
        ),
      'valor_parcelas'
    );
    // Data da primeira parcela
    if (dados?.proposta_financeira?.propostas_banco[0]?.data) {
      const date = new Date(dados.proposta_financeira.propostas_banco[0].data);
      if (!isNaN(date.getTime())) {
        refForm.current.setItem(date.toISOString().split('T')[0], 'data');
      }
    }
    // Número de parcelas
    refForm.current.setItem(
      String(
        !dados?.proposta_financeira?.propostas_banco[0]?.prazo_financiamento
          ? 36
          : dados?.proposta_financeira?.propostas_banco[0]?.prazo_financiamento
      ),
      'prazo_financiamento'
    );
    refForm.current.setItem(dados.seguro === true ? 'Sim' : 'Não', 'seguroSN');

    refForm.current.setItem(
      dados.seguro_assistencia === true ? 'Sim' : 'Não',
      'seguroAssisSN'
    );

    refForm.current.setItem(dados.iof === true ? 'Sim' : 'Não', 'iofSN');

    refForm.current.setItem(
      !dados.tab ? dados.taxas.perc_tab : dados.tab,
      'tabSN'
    );

    refForm.current.setItem(dados.tacSN ? 'Sim' : 'Não', 'tacSN');

    // Inicia com a tabela padrão selecionada
    const tabelaPadrao = dados?.taxas;
    refForm.current.setItem(tabelaPadrao?.nome || '', 'tabela');
    // Registr o campo da taxa padrao
    refForm.current.setItem(
      dados?.proposta_financeira?.propostas_banco[0]?.taxa === null
        ? tabelaPadrao?.perc_taxa || ''
        : dados?.proposta_financeira?.propostas_banco[0]?.taxa,
      'taxa'
    );
    // Realiza os calculos baseados na tabela padrão do banco
    // valor da carencia
    refForm.current.setItem(
      dados?.proposta_financeira?.propostas_banco[0]?.carencia === null
        ? tabelaPadrao?.carencia_padrao
        : dados?.proposta_financeira?.propostas_banco[0]?.carencia,
      'carencia'
    );

    setTimeout(async () => {
      await calc();
      await calcCet();
    }, 1000);
  }, [dados, calc]);

  const handleTacChange = useCallback(
    (val) => {
      if (val === 'Sim') {
        refForm.current.setItem(dados?.valor_tac_fixo ?? 900, 'tac');
      } else {
        refForm.current.setItem(0, 'tac');
      }

      handleValorEntradaOnBlur();
    },
    [handleValorEntradaOnBlur, dados?.valor_tac_fixo]
  );

  const defaultForm = useMemo(
    () => [
      {
        ref: 'valor_venda',
        placeholder: 'Valor Total da Venda (R$)',
        tress: true,
        type: 'money',
        disabled: true,
        transparent: false,
      },
      {
        ref: 'valor_entrada',
        placeholder: 'Valor Entrada (R$)',
        tress: true,
        type: 'money',
        required: false,
        disabled: false,
        onBlur: handleValorEntradaOnBlur,
      },
      {
        ref: 'valor_financiamento',
        placeholder: 'Valor Financiado (R$)',
        tress: true,
        type: 'money',
        disabled: true,
        transparent: false,
      },
      {
        ref: 'taxa',
        placeholder: 'Taxa ',
        tress: true,
        type: 'number',
        required: false,
        disabled: false,
        onBlur: handleFinancingRecalcOnBlur,
      },
      {
        ref: 'carencia',
        placeholder: 'Carência (dias)',
        tress: true,
        type: 'select',
        options: optionsCarencia,
        required: false,
        disabled: false,
        onBlur: handleFinancingRecalcOnBlur,
      },
      {
        ref: 'prazo_financiamento',
        placeholder: 'Prazo',
        tress: true,
        type: 'select',
        options: rangePrazo,
        required: false,
        disabled: false,
        onBlur: handleFinancingRecalcOnBlur,
      },

      {
        ref: 'tacSN',
        placeholder: 'TAC?',
        tress: true,
        type: 'select',
        options: optionsSN,
        required: false,
        disabled: false,
        transparent: true,
        onChange: handleTacChange,
      },
      {
        ref: 'seguroSN',
        placeholder: 'Seguro Prestamista?',
        tress: true,
        type: 'select',
        options: optionsSN,
        required: false,
        disabled: false,
        transparent: true,
        onBlur: handleFinancingRecalcOnBlur,
      },
      {
        ref: 'seguroAssisSN',
        placeholder: 'Seguro Assistência?',
        tress: true,
        type: 'select',
        options: optionsSN,
        required: false,
        disabled: false,
        transparent: true,
        onBlur: handleFinancingRecalcOnBlur,
      },
      {
        ref: 'tac',
        placeholder: 'TAC',
        tress: true,
        type: 'money',
        required: false,
        disabled: true,
        transparent: false,
      },
      {
        ref: 'valor_seguro',
        placeholder: 'Valor Seguro Prestamista',
        tress: true,
        type: 'select',
        required: false,
        disabled: true,
        transparent: false,
      },
      {
        ref: 'valor_seguro_assis',
        placeholder: 'Valor Seguro Assistência',
        tress: true,
        type: 'select',
        required: false,
        disabled: true,
        transparent: false,
      },
      {
        ref: 'iofSN',
        placeholder: 'IOF?',
        tress: true,
        type: 'select',
        options: optionsSN,
        required: false,
        disabled: false,
        transparent: true,
        onBlur: handleFinancingRecalcOnBlur,
      },
      {
        ref: 'tabSN',
        placeholder: '% TAB?',
        tress: true,
        type: 'select',
        options: optionsTacTab,
        required: false,
        disabled: false,
        transparent: true,
        onBlur: handleFinancingRecalcOnBlur,
      },

      {
        ref: 'cet',
        placeholder: 'CET %',
        tress: true,
        type: 'number',
        required: false,
        disabled: true,
        transparent: false,
      },
      {
        ref: 'iof',
        placeholder: 'IOF',
        tress: true,
        type: 'select',
        required: false,
        disabled: true,
        transparent: false,
      },
      {
        ref: 'tab',
        placeholder: 'TAB',
        tress: true,
        type: 'select',
        required: false,
        disabled: true,
        transparent: false,
      },

      {
        ref: 'valor_parcelas',
        placeholder: 'PMT',
        tress: true,
        type: 'money',
        required: false,
        transparent: true,
        readOnly: true,
        style: {
          backgroundColor: theme.palette.purple.main,
          color: theme.palette.purple.contrastText,
        },
      },
    ],
    [
      handleFinancingRecalcOnBlur,
      handleValorEntradaOnBlur,
      rangePrazo,
      handleTacChange,
    ]
  );

  const handleForm = useCallback(() => {
    const hasCredit = !!dados?.data_credito;
    const hasChecklist =
      !!dados?.observationCheckList && dados?.observationCheckList.length > 0;

    if (!hasCredit || !hasChecklist) {
      return defaultForm;
    }

    const formWithChecklist = handleFormWithChecklist(
      defaultForm,
      dados?.observationCheckList || []
    );

    return formWithChecklist;
  }, [dados?.data_credito, dados?.observationCheckList, defaultForm]);

  const close = () => setModal(null);

  useEffect(() => {
    if (!formOferta || formOferta.length === 0) {
      const form = handleForm();
      setFormOferta(form);

      // Preenche o formulário com os dados atuais da proposta
      handleInitializeForm();
      calc();
    }
  }, [formOferta, calc, handleForm, handleInitializeForm]);

  const formParecer = [
    {
      ref: 'obs',
      placeholder: 'Observações',
      full: true,
      required: true,
      disabled: false,
      maxRows: 4,
      multiline: true,
      height: 100,
    },
  ];

  const getLogChanges = ({ dados, payload, formValues }) => {
    const changes = [];

    const compareFields = [
      {
        oldValue: dados?.taxa,
        newValue: `${payload?.taxa}%`,
        text: (oldValue, newValue) => `Taxa de ${oldValue} para ${newValue}`,
      },
      {
        oldValue: dados?.entrada,
        newValue: parseCurrency(payload?.valor_entrada),
        text: (oldValue, newValue) => `Valor de ${oldValue} para ${newValue}`,
      },
      {
        oldValue: dados?.proposta_financeira?.propostas_banco?.[0]?.carencia,
        newValue: payload?.carencia,
        text: (oldValue, newValue) =>
          `Carência de ${oldValue} para ${newValue}`,
      },
      {
        oldValue: dados?.prazo,
        newValue: payload?.prazo_financiamento,
        text: (oldValue, newValue) => `Prazo de ${oldValue} para ${newValue}`,
      },
      {
        oldValue: dados?.seguro ? 'Sim' : 'Não',
        newValue: payload?.seguro_sn,
        text: (oldValue, newValue) =>
          `Seguro Prestamista de ${oldValue} para ${newValue}`,
      },
      {
        oldValue: dados?.seguro_assistencia ? 'Sim' : 'Não',
        newValue: payload?.seguroAssisSN,
        text: (oldValue, newValue) =>
          `Seguro Assistência de ${oldValue} para ${newValue}`,
      },
      {
        oldValue: dados?.iof ? 'Sim' : 'Não',
        newValue: payload?.iofSN,
        text: (oldValue, newValue) => `IOF de ${oldValue} para ${newValue}`,
      },
      {
        oldValue: !dados?.tac ? formValues?.tacSN : dados?.tac,
        newValue: payload?.tacSN,
        text: (oldValue, newValue) => `TAC de ${oldValue}% para ${newValue}%`,
      },
      {
        oldValue: !dados?.tab ? formValues?.tabSN : dados?.tab,
        newValue: payload?.tabSN,
        text: (oldValue, newValue) => `TAB de ${oldValue}% para ${newValue}%`,
      },
    ];

    compareFields.forEach(({ oldValue, newValue, text }) => {
      if (oldValue !== newValue) {
        changes.push(text(oldValue, newValue));
      }
    });

    return changes.length
      ? `Informação alterada: ${changes.join('. ')}`
      : 'Dados inalterados';
  };

  const save = async () => {
    const formValues = await refForm.current.getForm?.();
    const formParecerValues = await formRefParecer.current.getForm?.();
    if (!formValues || !formParecerValues) {
      return;
    }
    setLoading(true);

    const payload = {
      id: dados?.proposta_financeira.propostas_banco[0].id,
      valor_entrada: formValues.valor_entrada,
      valor_financiamento: unparseCurrency(formValues.valor_financiamento),
      valor_parcelas: unparseCurrency(formValues.valor_parcelas),
      prazo_financiamento: formValues.prazo_financiamento,
      taxa: formValues.taxa,
      carencia: formValues.carencia,
      tab: unparseCurrency(formValues.tab),
      iof: unparseCurrency(formValues.iof),
      tac: unparseCurrency(formValues.tac),
      seguro: unparseCurrency(formValues.valor_seguro),
      seguro_assistencia: unparseCurrency(formValues.valor_seguro_assis),
      cet: formValues.cet,
      data: moment(new Date())
        .add(formValues.carencia, 'days')
        .format('DD/MM/YYYY'),
      seguro_sn: formValues.seguroSN,
      seguroAssisSN: formValues.seguroAssisSN,
      iofSN: formValues.iofSN,
      tabSN: formValues.tabSN,
    };

    const payloadSeguro = {
      seguro: formValues.seguroSN === 'Sim' ? true : false,
      seguro_assistencia: formValues.seguroAssisSN === 'Sim' ? true : false,
      iof: formValues.iofSN === 'Sim' ? true : false,
      tacSN: parseSNtoBoolean(formValues.tacSN),
      tab: formValues.tabSN,
    };

    const newLogMessage = getLogChanges({ dados, payload, formValues });

    const newLogSystem = [
      ...dados?.parecer,
      {
        usuario: user.id,
        data: new Date(),
        message: newLogMessage,
        origem: 'Sistema',
      },
    ];

    const payloadLogComplete = {
      parecer: [
        ...newLogSystem,
        {
          usuario: user.id,
          data: new Date(),
          message: formParecerValues.obs,
          origem: 'Negócios',
        },
      ],
    };

    const updatePayload = {
      ...payloadLogComplete,
      ...payloadSeguro,
    };

    try {
      await UpdateCredito({ data: payload }, id);

      await Update({ data: updatePayload }, dados.id);

      toast.success('Registro salvo com sucesso!');
      close();
      reload?.();
      reloadContent?.();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Erro ao salvar');
    } finally {
      setLoading(false);
    }
  };

  const pareceresInvertidos = useMemo(() => {
    return dados?.parecer ? [...dados.parecer].reverse() : [];
  }, [dados?.parecer]);
  if (!dados) {
    return null;
  }

  return (
    <Wrapper onClose={onClose} title="Negócios" style={{ width: '70vw' }}>
      <Title small nomargin>
        Proposta de Negócios
      </Title>
      <AddProposalContainer>
        <FormCore ref={refForm} formItems={formOferta ?? defaultForm} />
      </AddProposalContainer>
      <div style={{ display: 'grid', width: '100%', gap: '24px' }}>
        <Touch
          onClick={() => setShowParecer(!showParecer)}
          style={{
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: '#f4f4f4',
            padding: '12px',
            borderRadius: '4px',
          }}
        >
          <Title nomargin small>
            Parecer: ({dados?.parecer.length})
          </Title>
          <span
            style={{
              transition: '0.3s',
              transform: showParecer ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          >
            ▼
          </span>
        </Touch>
        {pareceresInvertidos.length > 0 && showParecer && (
          <TimelineContainer>
            {pareceresInvertidos.map((parecer, idx) => (
              <TimelineItem key={idx}>
                <TimelineContent>
                  <div style={{ marginBottom: '4px' }}>
                    <strong style={{ color: '#2d3748', fontSize: '14px' }}>
                      {capitalize(parecer.usuario.username)}
                    </strong>
                    <span
                      style={{
                        color: '#718096',
                        fontSize: '12px',
                        marginLeft: '8px',
                      }}
                    >
                      {moment(parecer.data).format('DD/MM/YYYY HH:mm')} - (
                      {parecer.origem})
                    </span>
                  </div>
                  <span
                    style={{
                      fontSize: '15px',
                      color: '#4a5568',
                      lineHeight: '1.4',
                    }}
                  >
                    {parecer.message}
                  </span>
                </TimelineContent>
              </TimelineItem>
            ))}
          </TimelineContainer>
        )}
        <FormCore ref={formRefParecer} formItems={formParecer} />
        <CompanyButtonContainer>
          <Button onClick={close} color="lightgrey" border nospace>
            Cancelar
          </Button>
          <Button color="blue" loading={loading} onClick={save} nospace>
            {loading ? 'Salvando' : 'Salvar Alterações'}
          </Button>
        </CompanyButtonContainer>
      </div>
    </Wrapper>
  );
}
